import { prefersReducedMotion, yieldToMainThread } from '@theme/utilities';

/**
 * Shade hero boot-sequence animation.
 *
 * Three tasteful layers, all transform/opacity only, all gated behind
 * prefers-reduced-motion (in both CSS and JS), all CLS and LCP safe:
 *
 *   1. Terminal decode on the eyebrow ("FIELD 01 /"). The eyebrow is small,
 *      it is not the LCP element, and it is not SEO-critical. We scramble its
 *      characters in place at the EXACT original string length, then settle
 *      back to the server-rendered text. Because the character count never
 *      changes, the box width never changes, so there is zero layout shift.
 *      We capture the original text first and always restore it verbatim.
 *
 *   2. Staggered reveal of the hero content blocks (eyebrow, headline,
 *      bullets, badge, CTA). Each block translates up a few pixels and fades
 *      in, one after the other, like a field spec rendering line by line.
 *      Driven purely by a CSS class toggle plus per-block --reveal-index.
 *
 *   3. A very slow, very subtle Ken Burns drift on the hero image, applied to
 *      the media grid wrapper (not to the LCP img element itself, which keeps
 *      its natural opacity and paint timing). Transform only.
 *
 * The element self-instantiates: a single <shade-hero> tag is injected into
 * the body, so no core liquid section file needs editing. It finds the first
 * hero on the page via the stable .hero-wrapper class and wires an
 * IntersectionObserver to fire the sequence once when the hero is in view.
 *
 * Theme-editor safe: re-runs on shopify:section:load for the hero section and
 * disconnects any prior observer before re-observing.
 */
class ShadeHero extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {HTMLElement | null} */
  #hero = null;

  /** @type {number | null} */
  #decodeFrame = null;

  /** @type {HTMLElement | null} Eyebrow currently being decoded (for cleanup). */
  #decodeTarget = null;

  /** @type {string} Original eyebrow HTML, restored verbatim after the decode. */
  #decodeOriginalHTML = '';

  /** @type {boolean} */
  #fired = false;

  /** Characters used for the scramble pass. Monospace-friendly glyph set. */
  #glyphs = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789/_<>[]+*';

  connectedCallback() {
    // If the user opts out of motion, do nothing. The hero content is fully
    // server-rendered, so it is already visible at its natural state.
    if (prefersReducedMotion()) return;

    this.#attach();

    // Re-attach when the hero section is re-rendered by the theme editor.
    document.addEventListener('shopify:section:load', this.#onSectionLoad);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#cancelDecode();
    document.removeEventListener('shopify:section:load', this.#onSectionLoad);
  }

  /**
   * Cancel any in-flight rAF decode loop and restore the eyebrow to its exact
   * server-rendered markup (never left scrambled or aria-hidden). Safe to call
   * when no decode is running. Called from both disconnectedCallback and
   * #attach: a theme-editor re-render can land while a decode is still running,
   * and without this the old rAF keeps writing the eyebrow while a second loop
   * starts, clobbering the shared #decodeFrame handle and stranding the text.
   */
  #cancelDecode() {
    if (this.#decodeFrame !== null) {
      cancelAnimationFrame(this.#decodeFrame);
      this.#decodeFrame = null;
    }
    if (this.#decodeTarget) {
      this.#decodeTarget.innerHTML = this.#decodeOriginalHTML;
      this.#decodeTarget.removeAttribute('aria-hidden');
      this.#decodeTarget = null;
    }
  }

  /**
   * Re-wire the observer when the hero section reloads in the theme editor.
   * @param {Event} event
   */
  #onSectionLoad = (event) => {
    const target = /** @type {HTMLElement} */ (/** @type {CustomEvent} */ (event).target);
    if (target && target.querySelector?.('.hero-wrapper, .hero')) {
      this.#fired = false;
      this.#attach();
    }
  };

  /** Find the first hero on the page and observe it. */
  #attach() {
    // A re-render (theme editor) can land while a decode rAF is still in flight;
    // cancel it and restore the eyebrow before re-observing so we never run two
    // decode loops on the same element.
    this.#cancelDecode();
    this.#observer?.disconnect();

    // Stable, schema-defined section class. Fall back to the hero block itself.
    const hero = /** @type {HTMLElement | null} */ (
      document.querySelector('.hero-wrapper .hero') || document.querySelector('.hero')
    );

    if (!hero) return;
    this.#hero = hero;

    // Tag the hero so the CSS layers (reveal + Ken Burns) can scope to it
    // without us editing the liquid section file.
    hero.dataset.shadeHero = 'idle';

    this.#observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry || !entry.isIntersecting || this.#fired) return;

        this.#fired = true;
        this.#observer?.disconnect();
        this.#run();
      },
      { threshold: 0.25 }
    );

    this.#observer.observe(hero);
  }

  /** Kick off the reveal and decode once the hero is in view. */
  async #run() {
    const hero = this.#hero;
    if (!hero) return;

    // Yield so the reveal transition starts on a clean frame, mirroring the
    // jumbo-text pattern (which yields before adding its visible class).
    await yieldToMainThread();

    // Layer 2 + 3: stagger reveal and Ken Burns drift are pure CSS, switched
    // on by this single attribute flip.
    hero.dataset.shadeHero = 'active';

    // Layer 1: terminal decode on the eyebrow, the first content block.
    this.#decodeEyebrow(hero);
  }

  /**
   * Scramble then settle the eyebrow text in place, at fixed length.
   * @param {HTMLElement} hero
   */
  #decodeEyebrow(hero) {
    const content = hero.querySelector('.group-block-content');
    if (!content) return;

    const firstBlock = /** @type {HTMLElement | null} */ (content.querySelector(':scope > .text-block'));
    if (!firstBlock) return;

    // The actual text node lives inside the inner element (e.g. <p>).
    const target = /** @type {HTMLElement | null} */ (firstBlock.querySelector('p, span')) || firstBlock;

    // Never scramble a heading: that would mutate H1/LCP/keyword text. The
    // eyebrow is always a <p>; this guards against future block reordering.
    if (/^H[1-6]$/.test(target.tagName)) return;

    const finalText = target.textContent ?? '';
    const length = finalText.length;
    if (length === 0) return;

    // Preserve any inline markup and keep the scramble out of the a11y tree so
    // screen readers never announce the intermediate gibberish frames.
    this.#decodeTarget = target;
    this.#decodeOriginalHTML = target.innerHTML;
    target.setAttribute('aria-hidden', 'true');

    // Which character index has settled to its final glyph. Indices below this
    // show the real character; indices at or above it show a random glyph.
    let settled = 0;
    const totalFrames = Math.min(length + 6, 26);
    let frame = 0;

    const tick = () => {
      // Reveal real characters progressively from left to right.
      settled = Math.floor((frame / totalFrames) * length);

      let out = '';
      for (let i = 0; i < length; i++) {
        const realChar = finalText[i];
        // Preserve spaces and the literal separators so the shape reads true.
        if (i < settled || realChar === ' ' || realChar === '/' || realChar === '_') {
          out += realChar;
        } else {
          out += this.#glyphs[(Math.random() * this.#glyphs.length) | 0];
        }
      }

      target.textContent = out;
      frame += 1;

      if (frame <= totalFrames) {
        this.#decodeFrame = requestAnimationFrame(tick);
      } else {
        // Always restore the exact server-rendered markup and re-expose it to
        // assistive tech. Never leave a scrambled or truncated string behind.
        target.innerHTML = this.#decodeOriginalHTML;
        target.removeAttribute('aria-hidden');
        this.#decodeTarget = null;
        this.#decodeFrame = null;
      }
    };

    this.#decodeFrame = requestAnimationFrame(tick);
  }
}

// Register the element once.
if (!customElements.get('shade-hero')) {
  customElements.define('shade-hero', ShadeHero);
}

// Self-instantiate exactly one instance, so no liquid section edit is needed.
// Idempotent: never inject a second tag.
if (!document.querySelector('shade-hero')) {
  document.body.appendChild(document.createElement('shade-hero'));
}
